import { useImpersonation } from '@/contexts/ImpersonationContext';
import { hasMinRole, type SystemRole } from '@/lib/constants';

export interface RBACPermissions {
  role: SystemRole;
  isAdmin: boolean;
  isSuperadmin: boolean;
  canViewFinancials: boolean;
  canViewAllEmployees: boolean;
  canViewAllProjects: boolean;
  canManageSettings: boolean;
}

/**
 * Hook for role-based access control (RBAC).
 *
 * Hierarchy: external < user < admin < superadmin
 *
 * Reads from useImpersonation so admins see the impersonated user's permissions
 * while impersonating. Use this hook everywhere except true auth gating.
 */
export function useRBAC(): RBACPermissions {
  const { activeUser } = useImpersonation();
  const role = (activeUser?.role ?? 'user') as SystemRole;
  const isSuperadmin = role === 'superadmin';
  const isAdmin = hasMinRole(role, 'admin');

  return {
    role,
    isAdmin,
    isSuperadmin,
    canViewFinancials: isAdmin,
    canViewAllEmployees: isAdmin,
    canViewAllProjects: isAdmin,
    canManageSettings: isAdmin,
  };
}
